import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { motion, AnimatePresence } from 'framer-motion';

const interestOptions = [
  { value: 'services', label: 'Services Inquiry' },
  { value: 'product', label: 'Product Inquiry' },
  { value: 'resources', label: 'Resources Inquiry' },
  { value: 'partnership', label: 'Partnership Opportunity' },
  { value: 'support', label: 'Technical Support' },
  { value: 'other', label: 'Other' },
];

export const ContactForm = ({ className = '' }) => {
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    interest: '',
    message: '',
  });

  // Prefill from URL params - use useMemo instead of useEffect to avoid cascading renders
  const initialFormDataRef = React.useRef(null);
  if (initialFormDataRef.current === null) {
    const interest = searchParams.get('interest');
    const product = searchParams.get('product');
    const manufacturer = searchParams.get('manufacturer');
    
    let newData = { ...formData };
    
    if (interest) {
      const matchingOption = interestOptions.find(
        (opt) => opt.label.toLowerCase().includes(interest.toLowerCase())
      );
      if (matchingOption) {
        newData.interest = matchingOption.value;
      }
    }

    if (product || manufacturer) {
      newData.message = `I am interested in ${product || 'a product'}${manufacturer ? ` from ${manufacturer}` : ''}. Please provide more information and a quote.`;
    }
    
    initialFormDataRef.current = newData;
  }

  useEffect(() => {
    if (initialFormDataRef.current && 
        (initialFormDataRef.current.interest || initialFormDataRef.current.message)) {
      setFormData(initialFormDataRef.current);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (value) => {
    setFormData((prev) => ({ ...prev, interest: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Frontend-only mock submission
    console.log('Form submitted:', formData);
    setSubmitted(true);
    // Store in localStorage as a mock
    const submissions = JSON.parse(localStorage.getItem('voitech_inquiries') || '[]');
    submissions.push({ ...formData, timestamp: new Date().toISOString() });
    localStorage.setItem('voitech_inquiries', JSON.stringify(submissions));
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`text-center py-12 ${className}`}
      >
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-success" />
        </div>
        <h3 className="text-2xl font-semibold text-foreground mb-2">
          Thanks for reaching out!
        </h3>
        <p className="text-muted-foreground mb-6">
          We&apos;ll get back to you within 24-48 hours.
        </p>
        <Button variant="outline" onClick={() => setSubmitted(false)}>
          Send Another Message
        </Button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-6 ${className}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">Full Name *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Doe"
            required
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@company.com"
            required
          />
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+256 700 000000"
          />
        </div>

        {/* Company */}
        <div className="space-y-2">
          <Label htmlFor="company">Company / Organization</Label>
          <Input
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Your Company Ltd"
          />
        </div>
      </div>

      {/* Interest */}
      <div className="space-y-2">
        <Label htmlFor="interest">Area of Interest *</Label>
        <Select value={formData.interest} onValueChange={handleInterestChange} required>
          <SelectTrigger>
            <SelectValue placeholder="Select your area of interest" />
          </SelectTrigger>
          <SelectContent>
            {interestOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Message */}
      <div className="space-y-2">
        <Label htmlFor="message">Message *</Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us about your requirements or questions..."
          rows={5}
          required
        />
      </div>

      {/* Submit */}
      <Button type="submit" variant="hero" size="xl" className="w-full md:w-auto">
        <Send className="w-5 h-5 mr-2" />
        Send Message
      </Button>
    </form>
  );
};
